import classNames from 'classnames';
import { type FC, type ReactNode, useState } from 'react';
import { Link, type LinkProps } from 'react-router';

interface NavigationLoadingLinkProps extends Omit<LinkProps, 'to'> {
  to: string;
  children: ReactNode;
  isActive?: boolean;
  loadingIcon?: ReactNode;
  className?: string;
}

/**
 * Navigation link component that shows loading state during navigation
 * Provides immediate visual feedback when clicked
 */
export const NavigationLoadingLink: FC<NavigationLoadingLinkProps> = ({
  to,
  children,
  isActive: _isActive = false,
  loadingIcon,
  className,
  onClick,
  ...props
}) => {
  const [isClicked, setIsClicked] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Only show loading if navigating to a different page
    if (window.location.pathname !== to) {
      setIsClicked(true);

      // Reset clicked state quickly to avoid sticking
      setTimeout(() => {
        setIsClicked(false);
      }, 150);
    }

    onClick?.(event);
  };

  const showLoading = isClicked && window.location.pathname !== to;

  return (
    <Link
      to={to}
      className={classNames(className, {
        'opacity-70': showLoading,
      })}
      onClick={handleClick}
      {...props}
    >
      {showLoading && loadingIcon ? (
        <div className="flex items-center gap-2">
          {loadingIcon}
          <span className="text-xs">Loading...</span>
        </div>
      ) : (
        children
      )}
    </Link>
  );
};
